// Lead detail — the right pane of 04 §2.
// Executive intelligence cockpit for prospective B2B service leads.

import { useState } from 'react';
import { FINDING_CATEGORIES, type FindingCategory } from '@leadg/shared';
import { StateIcon, StateLegend } from '../../components/StateIcon.js';
import { ActivityTab } from './ActivityTab.js';
import { Dossier } from './Dossier.js';
import { DossierExport } from './export/ExportButtons.js';
import { LeadHeader } from './LeadDetailHeader.js';
import { LegalTab } from './LegalTab.js';
import { useDossier } from './useDossier.js';
import {
  useFindings,
  useLead,
  usePlacesCache,
  useScreenshot,
  type FindingRow,
} from './useLeads.js';

const CATEGORY_LABELS: Record<FindingCategory, string> = {
  legal: 'Правни и съответствие',
  capabilities: 'Възможности и онлайн резервации',
  freshness: 'Актуалност и свежест',
  performance: 'Скорост и мобилно изживяване',
  visibility: 'Видимост и доверие на клиентите',
};

const TABS = [
  { key: 'sales', label: 'Продажби (Досие)' },
  { key: 'audit', label: 'Одит и констатации' },
  { key: 'legal', label: 'Правни и регистър' },
  { key: 'activity', label: 'Активност и бележки' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function PaneEmpty({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="empty-state">
      <p className="empty-state__title">{title}</p>
      <p className="empty-state__desc">{desc}</p>
    </div>
  );
}

function CategorySection({
  category,
  findings,
}: {
  category: FindingCategory;
  findings: FindingRow[];
}) {
  if (findings.length === 0) return null;
  return (
    <section className="cat">
      <h3 className="cat__title">{CATEGORY_LABELS[category]}</h3>
      <div className="cat__findings">
        {findings.map((f) => (
          <article key={f.id} className="finding-card">
            <div className="finding-card__icon">
              <StateIcon state={f.state} />
            </div>
            <div className="finding-card__body">
              <h4 className="finding-card__title">{f.title_bg ?? f.check_key}</h4>
              {f.detail_bg && <p className="finding-card__detail">{f.detail_bg}</p>}
              {f.evidence_text && (
                <p className="finding-card__detail">Доказателство: {f.evidence_text}</p>
              )}
              {f.consequence_bg && <p className="finding-card__detail">{f.consequence_bg}</p>}
              {f.improvement_bg && (
                <div className="finding-card__fix">
                  <strong>→ Какво предлагаме:</strong> {f.improvement_bg}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function AuditView({ leadId }: { leadId: number }) {
  const { data: findings = [], error } = useFindings(leadId);
  const { data: screenshotData } = useScreenshot(leadId);
  const screenshotUrl = screenshotData?.url ?? null;
  if (error) return <PaneEmpty title="Грешка при зареждане на одита" desc={error.message} />;
  if (findings.length === 0) {
    return <PaneEmpty title="Одитът не е правен" desc="Пуснете одит за този обект от Пайплайн." />;
  }
  return (
    <div className="lead-audit">
      <div className="lead-audit__legendbox">
        <StateLegend />
      </div>
      {screenshotUrl && (
        <div className="lead-audit__screenshot">
          <img src={screenshotUrl} alt="Мобилен изглед на сайта" />
        </div>
      )}
      {FINDING_CATEGORIES.map((category) => (
        <CategorySection
          key={category}
          category={category}
          findings={findings.filter((f) => f.category === category)}
        />
      ))}
    </div>
  );
}

function SalesView({ leadId, name }: { leadId: number; name: string }) {
  const { data: dossier, document, error } = useDossier(leadId);
  if (error) return <PaneEmpty title="Грешка при зареждане на досието" desc={error.message} />;
  if (!dossier) {
    return <PaneEmpty title="Досието не е генерирано" desc="Генерирайте досие от Пайплайн." />;
  }
  return (
    <div className="lead-sales">
      <Dossier content={dossier} meta={document} />
      <div className="lead-sales__export">
        <DossierExport leadId={leadId} name={name} />
      </div>
    </div>
  );
}

export function LeadDetail({ leadId }: { leadId: number | null }) {
  const { data: lead, isLoading } = useLead(leadId);
  const { data: cached } = usePlacesCache(leadId);
  const [tab, setTab] = useState<TabKey>('sales');

  if (leadId === null) {
    return (
      <PaneEmpty
        title="Изберете обект от списъка"
        desc="Кликнете върху обект вляво за пълен анализ."
      />
    );
  }
  if (isLoading) return <div className="boot">Зареждане на досие…</div>;
  if (!lead) return <div className="empty-state">Обектът не е намерен.</div>;

  return (
    <div className="lead-detail">
      <LeadHeader lead={lead} cached={cached} />
      <nav className="lead-detail__nav">
        <div className="tabs-nav">
          {TABS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={`tabs-nav__btn${tab === entry.key ? ' tabs-nav__btn--active' : ''}`}
              onClick={() => setTab(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="lead-detail__content">
        {tab === 'sales' && <SalesView leadId={leadId} name={lead.business_name} />}
        {tab === 'audit' && <AuditView leadId={leadId} />}
        {tab === 'legal' && <LegalTab leadId={leadId} parentLeadId={lead.parent_lead_id} />}
        {tab === 'activity' && <ActivityTab leadId={leadId} notes={lead.my_notes ?? ''} />}
      </div>
    </div>
  );
}
